import {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { cohortsApi, preferencesApi } from '../api';
import { AppDataContext } from '../context';

/** Server-side source of truth (`UserPreference.KEY_REGEX` allows `[-_a-zA-Z0-9]+`). */
export const FACILITATOR_NOTICE_PREFERENCE_KEY = 'legatum-facilitator-notice-seen';
/** Per-browser short-circuit; holds the username so a second account is unaffected. */
export const FACILITATOR_NOTICE_MIRROR_KEY = 'facilitator-promotion-notice';
/** Per-tab marker "checked, not a facilitator"; holds the username, dropped on courseware exit. */
export const FACILITATOR_NOTICE_SESSION_KEY = 'facilitator-promotion-notice-checked';

const COURSEWARE_PATH_PREFIX = '/course-working/';

const isCoursewarePath = (pathname: string) => pathname.startsWith(COURSEWARE_PATH_PREFIX);

export interface UseFacilitatorPromotionNoticeResult {
  shouldShow: boolean;
  dismiss: () => void;
}

/**
 * Announces the Facilitator promotion at most once per account.
 * The role is granted backend-side, so it is observed on mount and on courseware exit.
 */
export const useFacilitatorPromotionNotice = (): UseFacilitatorPromotionNoticeResult => {
  const { authenticatedUser } = useContext(AppDataContext);
  const username = authenticatedUser?.username ?? null;
  const { pathname } = useLocation();

  const [shouldShow, setShouldShow] = useState<boolean>(false);
  const settledRef = useRef<boolean>(false);
  const inFlightRef = useRef<boolean>(false);
  const previousPathRef = useRef<string | null>(null);

  const observe = useCallback(() => {
    if (!username || settledRef.current || inFlightRef.current) {
      return;
    }

    if (localStorage.getItem(FACILITATOR_NOTICE_MIRROR_KEY) === username) {
      settledRef.current = true;
      return;
    }

    if (sessionStorage.getItem(FACILITATOR_NOTICE_SESSION_KEY) === username) {
      return;
    }

    inFlightRef.current = true;

    preferencesApi.getUserPreference(
      username,
      FACILITATOR_NOTICE_PREFERENCE_KEY,
      (value) => {
        inFlightRef.current = false;

        // Dismissed while in flight: re-showing would break the "at most once" guarantee.
        if (settledRef.current) {
          return;
        }

        if (value === 'true') {
          settledRef.current = true;
          localStorage.setItem(FACILITATOR_NOTICE_MIRROR_KEY, username);
          return;
        }

        inFlightRef.current = true;
        cohortsApi.getFacilitatorStatus(
          (status) => {
            inFlightRef.current = false;

            if (settledRef.current) {
              return;
            }

            // Co-facilitators cannot create cohorts, so the congratulation would be false.
            if (status?.facilitator_type === 'facilitator') {
              setShouldShow(true);
            } else {
              sessionStorage.setItem(FACILITATOR_NOTICE_SESSION_KEY, username);
            }
          },
          () => { inFlightRef.current = false; },
        );
      },
      // Flag unknown: stay silent. "At most once" outranks "always shown".
      () => { inFlightRef.current = false; },
    );
  }, [username]);

  useEffect(() => {
    observe();
  }, [observe]);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = pathname;

    if (previousPath === null || previousPath === pathname) {
      return;
    }

    if (isCoursewarePath(previousPath) && !isCoursewarePath(pathname)) {
      // A promotion can only have landed in the courseware: re-check on the way out.
      sessionStorage.removeItem(FACILITATOR_NOTICE_SESSION_KEY);
      observe();
    }
  }, [pathname, observe]);

  const dismiss = useCallback(() => {
    setShouldShow(false);
    settledRef.current = true;

    if (!username) {
      return;
    }

    localStorage.setItem(FACILITATOR_NOTICE_MIRROR_KEY, username);

    // Never block dismissal on the write; worst case the notice reappears on another device.
    Promise.resolve(
      preferencesApi.setUserPreference(username, FACILITATOR_NOTICE_PREFERENCE_KEY, 'true'),
    ).catch(() => {});
  }, [username]);

  return { shouldShow, dismiss };
};
